import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle, Cable, Save, RotateCcw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Collapsible "Wings Connection" card shown on the Sync Monitor page.
 * Lets an operator rotate the Wings API token, base URL, page size and concurrency
 * without editing backend .env. Values live in a Mongo `wings_config` document
 * and override the env-based defaults at runtime.
 */
export default function WingsConfigCard() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ base_url: "", token: "", page_size: "", concurrency: "" });
  const [showToken, setShowToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const res = await api.get("/sync/wings-config");
    setConfig(res.data);
    setDraft({
      base_url: res.data.base_url || "",
      token: "",
      page_size: String(res.data.page_size ?? 50000),
      concurrency: String(res.data.concurrency ?? 8),
    });
    setTestResult(null);
  };

  useEffect(() => { load(); }, []);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {};
      if (draft.base_url && draft.base_url !== config.base_url) payload.base_url = draft.base_url;
      if (draft.token) payload.token = draft.token;
      const res = await api.post("/sync/wings-config/test", payload);
      setTestResult(res.data);
      if (res.data.ok) {
        toast.success(`Connection OK · ${res.data.master_count || 0} products · ${res.data.stock_count || 0} stock rows`);
      } else {
        toast.error(res.data.error || "Connection failed");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Connection test failed";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (draft.base_url && draft.base_url !== config.base_url) payload.base_url = draft.base_url;
      if (draft.token) payload.token = draft.token;
      if (draft.page_size && parseInt(draft.page_size, 10) !== config.page_size) payload.page_size = parseInt(draft.page_size, 10);
      if (draft.concurrency && parseInt(draft.concurrency, 10) !== config.concurrency) payload.concurrency = parseInt(draft.concurrency, 10);
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save.");
        return;
      }
      await api.put("/sync/wings-config", payload);
      toast.success("Wings configuration saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to the .env defaults and clear the saved override?")) return;
    setResetting(true);
    try {
      await api.delete("/sync/wings-config");
      toast.success("Config reset to .env defaults");
      await load();
    } catch (err) {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  if (!config) return null;

  const source = config.has_custom_token ? "Runtime override (Mongo)" : "Environment defaults (.env)";

  return (
    <Surface padded={false} data-testid="wings-config-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
        data-testid="wings-config-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center">
            <KeyRound className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="text-left">
            <h3 className="font-heading font-semibold text-gray-900">Wings Connection</h3>
            <p className="text-xs text-gray-500">
              {source} · Token {config.token_masked || "(not set)"} · Page {config.page_size} · Concurrency {config.concurrency}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-200 p-6 space-y-5">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Base URL</Label>
              <Input
                value={draft.base_url}
                onChange={(e) => setDraft({ ...draft, base_url: e.target.value })}
                placeholder="https://wingsapiservices.wingsoncloud.co.in/TharaAPIs/get_data"
                className="font-mono text-sm"
                data-testid="wings-config-base-url"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                Bearer Token
                {config.has_custom_token && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] normal-case tracking-normal">
                    Custom saved
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  type={showToken ? "text" : "password"}
                  value={draft.token}
                  onChange={(e) => setDraft({ ...draft, token: e.target.value })}
                  placeholder={config.token_masked ? `Currently: ${config.token_masked}` : "Paste Wings API bearer token"}
                  className="font-mono text-sm pr-10"
                  data-testid="wings-config-token"
                />
                <button
                  type="button"
                  onClick={() => setShowToken((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-400 hover:text-gray-700 flex items-center justify-center"
                  aria-label="Toggle token visibility"
                >
                  {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Leave blank to keep the existing token. Only paste a new one when rotating.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Page Size</Label>
                <Input
                  type="number"
                  min={1}
                  max={50000}
                  value={draft.page_size}
                  onChange={(e) => setDraft({ ...draft, page_size: e.target.value })}
                  className="font-mono"
                  data-testid="wings-config-page-size"
                />
                <p className="text-[11px] text-gray-500">Rows per page (max 50 000)</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Concurrency</Label>
                <Input
                  type="number"
                  min={1}
                  max={32}
                  value={draft.concurrency}
                  onChange={(e) => setDraft({ ...draft, concurrency: e.target.value })}
                  className="font-mono"
                  data-testid="wings-config-concurrency"
                />
                <p className="text-[11px] text-gray-500">Parallel page fetches (1-32)</p>
              </div>
            </div>
          </div>

          {testResult && (
            <div className={`rounded-md border px-4 py-3 flex items-start gap-3 ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`} data-testid="wings-config-test-result">
              {testResult.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <div className="text-sm">
                {testResult.ok ? (
                  <>
                    <p className="font-semibold">Connection successful</p>
                    <p className="text-xs mt-0.5">
                      Wings reports {testResult.master_count?.toLocaleString("en-IN")} product-master rows and {testResult.stock_count?.toLocaleString("en-IN")} stock rows available.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">Connection failed</p>
                    <p className="text-xs mt-0.5">{testResult.error}</p>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing}
                data-testid="wings-config-test"
              >
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cable className="h-4 w-4 mr-2" />}
                Test Connection
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={resetting || !config.has_custom_token}
                data-testid="wings-config-reset"
              >
                {resetting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                Reset to .env
              </Button>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="wings-config-save"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </Surface>
  );
}
